import React, {forwardRef, useId, useMemo} from 'react'
import {Box, Flex, AspectRatio, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import Slider from '../../components/cms/slider'
import ModuleContainer from '../../components/cms/module-container'
import Asset from '../../components/cms/asset'
import {galleryRecipe, slideRecipe} from './index.recipe'
import Heading from '../../components/cms/heading'
import Copy from '../../components/cms/copy'
import {hasValidString, isVideoAsset} from '../../utils/utils'
import Fade from '../../components/cms/fade'
import RichText from '../../components/cms/rich-text'

const TextContainer = ({
    variant,
    headline,
    headingLevel,
    sliderId,
    index,
    hideHeadline,
    copy,
    slideRecipeFn
}) => {
    const styles = useMemo(() => slideRecipeFn({variant}), [slideRecipeFn, variant])

    return (
        <Box {...styles?.textContainer}>
            {headline && (
                <RichText
                    as={`h${headingLevel + 1}`}
                    id={`${sliderId}-headline-${index}`}
                    className={hideHeadline ? 'sr-only' : undefined}
                    {...styles?.headline}
                >
                    {headline}
                </RichText>
            )}
            {copy?.text && <Copy alignment="inherit" variant="gallery" {...copy} />}
        </Box>
    )
}

TextContainer.propTypes = {
    variant: PropTypes.string,
    headline: PropTypes.string,
    headingLevel: PropTypes.number,
    sliderId: PropTypes.string,
    index: PropTypes.number,
    hideHeadline: PropTypes.bool,
    copy: PropTypes.object,
    slideRecipeFn: PropTypes.func
}

const Slide = forwardRef(
    (
        {
            darken,
            asset,
            headline,
            hideHeadline,
            copy,
            sliderId,
            headingLevel,
            index,
            fixedItemText,
            slideRecipeFn,
            ...props
        },
        ref
    ) => {
        const videoRef = React.useRef(null)

        const styles = useMemo(
            () => slideRecipeFn({darken: darken.replace('%', '')}),
            [slideRecipeFn, darken]
        )

        const modifiedAsset = useMemo(
            () => ({
                ...asset,
                videoControls: false
            }),
            [asset]
        )

        return (
            <Flex {...props} {...styles.root} data-is-video={asset.type !== 'image'}>
                {!fixedItemText ? (
                    <TextContainer
                        variant="static"
                        headline={headline}
                        headingLevel={headingLevel}
                        sliderId={sliderId}
                        index={index}
                        hideHeadline={hideHeadline}
                        copy={copy}
                        slideRecipeFn={slideRecipeFn}
                    />
                ) : (
                    <Box />
                )}

                <Box ref={ref} {...styles.itemContainer}>
                    {/*top layer*/}
                    {headline && <Box {...styles.topLayer}></Box>}
                    {/*bottom layer*/}
                    {!isVideoAsset(asset) && <Box {...styles.bottomLayer}></Box>}
                    <AspectRatio
                        ratio={{base: 1, s: 2}}
                        // fix broken scaling of image when using fractional density - causes the image to draw outside the container
                        borderBottomWidth={{base: '1px', s: '0'}}
                        borderColor="transparent"
                        borderStyle="solid"
                    >
                        <Asset
                            asset={modifiedAsset}
                            videoRef={videoRef}
                            objectFit="cover"
                            widths={{base: '100vw', s: '88vw', l: 1680}}
                            aspectRatio={{base: 1, s: 2}}
                            variant="gallery"
                        />
                    </AspectRatio>
                </Box>
            </Flex>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    Slide.displayName = 'Slide'
}

Slide.propTypes = {
    darken: PropTypes.string,
    asset: PropTypes.object,
    headline: PropTypes.string,
    copy: PropTypes.object,
    sliderId: PropTypes.string,
    hideHeadline: PropTypes.bool,
    headingLevel: PropTypes.number,
    index: PropTypes.number,
    fixedItemText: PropTypes.bool,
    slideRecipeFn: PropTypes.func
}

const Gallery = React.forwardRef(function Gallery(
    {
        moduleId,
        moduleType,
        pageId,
        headingLevel,
        heading,
        copy,
        galleryItems,
        anchor,
        background,
        spacing,
        fixedItemText
    },
    ref
) {
    const sliderId = useId()
    const slideRecipeFn = useSlotRecipe({recipe: slideRecipe})
    const galleryRecipeFn = useSlotRecipe({recipe: galleryRecipe})

    const styles = useMemo(() => galleryRecipeFn(), [galleryRecipeFn])

    const ariaLabel = useMemo(
        () =>
            [
                hasValidString(heading.subline) && `${heading.subline} -`,
                heading.headline1,
                heading.headline2
            ]
                .filter(hasValidString)
                .join(' '),
        [heading.subline, heading.headline1, heading.headline2]
    )

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={anchor}
            background={background}
            spacing={spacing}
            {...styles.moduleContainer}
            heading={heading}
            ref={ref}
        >
            <Flex {...styles.textContainer}>
                <Fade>
                    <Heading {...heading} headingLevel={headingLevel} alignment="inherit"></Heading>
                </Fade>
                <Fade>
                    <Copy alignment="inherit" {...copy} />
                </Fade>
            </Flex>

            <Box pt={{base: 'spacing-40', s: 'spacing-80'}} position="relative">
                {fixedItemText && (
                    <TextContainer
                        variant="fixed"
                        headline={galleryItems?.[0].headline}
                        headingLevel={headingLevel}
                        sliderId={sliderId}
                        index={0}
                        hideHeadline={galleryItems?.[0].hideHeadline}
                        copy={galleryItems?.[0].copy}
                        slideRecipeFn={slideRecipeFn}
                    />
                )}
                <Slider variant="gallery" sliderId={sliderId} sliderLabel={ariaLabel}>
                    {galleryItems.map((item, index) => (
                        <Slide
                            {...item}
                            key={index}
                            index={index}
                            sliderId={sliderId}
                            headingLevel={headingLevel}
                            fixedItemText={fixedItemText}
                            slideRecipeFn={slideRecipeFn}
                        ></Slide>
                    ))}
                </Slider>
            </Box>
        </ModuleContainer>
    )
})

if (process.env.NODE_ENV !== 'production') {
    Gallery.displayName = 'Gallery'
}

Gallery.propTypes = {
    alignment: PropTypes.string,
    headingLevel: PropTypes.number,
    heading: PropTypes.object,
    copy: PropTypes.object,
    galleryItems: PropTypes.array,
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    spacing: PropTypes.oneOf(['default', 'reduced', 'min', 'min-top', 'min-bottom']),
    fixedItemText: PropTypes.bool
}

export default Gallery
